import { t } from 'i18next';
import { isIOS, isMacOs } from 'react-device-detect';
import { useEffect, useMemo, useState } from 'react';
import { usePreventScroll } from '../utils/UIUtils';
import { useColorMode } from '@chakra-ui/react';

export default function ForceToUpdatePage() {
  usePreventScroll();
  const { colorMode } = useColorMode();

  const redirectToStore = () => {
    if (isIOS || isMacOs) {
      window.location.href =
        'https://apps.apple.com/kr/app/fanhearts-%EB%8D%95%EC%A7%88%EC%9D%84-%ED%8E%B8%ED%95%98%EA%B2%8C/id6449088562';
    } else {
      window.location.href = 'https://play.google.com/store/apps/details?id=com.peacecover.pocapan';
    }
  };

  const bgUrl = useMemo(
    () => `/assets/force_to_update/bg_ask_update_${colorMode}.png`,
    [colorMode]
  );
  const [bgReady, setBgReady] = useState(false);

  useEffect(() => {
    setBgReady(false);
    const img = new Image();
    img.decoding = 'async';
    img.fetchPriority = 'high';
    img.src = bgUrl;
    img.onload = () => {
      setBgReady(true);
    };
    img.onerror = () => {
      setBgReady(true);
    };
  }, [bgUrl]);

  return (
    <div
      className="relative min-h-screen h-screen grid grid-rows-[auto,1fr]"
      style={{
        backgroundColor: colorMode === 'dark' ? '#0D0E12' : '#FFFFFF'
      }}
    >
      <img
        src={bgUrl}
        alt="background"
        aria-hidden
        className="pointer-events-none select-none absolute inset-0 w-full h-full object-cover"
        style={{
          opacity: bgReady ? 1 : 0,
          transition: 'opacity 160ms ease-out'
        }}
      />

      <div className="relative z-10 flex flex-col items-center justify-center h-screen">
        <img
          src="/assets/logos/logo_icon.png"
          alt="logo"
          className="w-[120px] h-[120px]"
          loading="eager"
          decoding="async"
        />

        <div className="px-[24px] py-[10px] flex flex-col items-center gap-[14px]">
          <h1
            className="text-[26px] font-bold whitespace-pre-line leading-tight text-center font-sans"
            style={{ color: colorMode === 'dark' ? '#FAFAFA' : '#222222' }}
          >
            {t('forceToUpdate.pageTitle')}
          </h1>
          <p
            className="text-[15px] font-medium whitespace-pre-line leading-tight text-center font-sans"
            style={{ color: colorMode === 'dark' ? '#7A7A7D' : '#888888' }}
          >
            {t('forceToUpdate.description')}
          </p>
        </div>

        <div className="mt-[10px]">
          <div className="relative z-10">
            <div
              className="absolute inset-0 z-[-1] rounded-[100px]"
              style={buttonBoxColorStyle.glowLayerStyle[colorMode]}
            />
            <button
              className="px-[24px] py-[16px] border-none rounded-[100px]"
              type="button"
              onClick={redirectToStore}
              style={buttonBoxColorStyle.bgColorStyle[colorMode]}
            >
              <p
                className="text-[17px] font-semibold leading-[20px]"
                style={buttonBoxColorStyle.textColorStyle[colorMode]}
              >
                {t('forceToUpdate.button')}
              </p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

 export const buttonBoxColorStyle = {
  glowLayerStyle: {
    light: {
      opacity: 0.8,
      background:
        'conic-gradient(from 270deg at 50% 50%, #94A5F9 0deg, #5CDBF6 108deg, #FFE175 180deg, #D671F6 288deg, #9F9DF9 360deg)',
      filter: 'blur(7.5px)'
    },
    dark: {
      opacity: 0.8,
      background:
        'conic-gradient(from 135deg at 50% 50%, #E5B400 0deg, #CB3AF3 102.6deg, #03D3FF 232.2deg, #E5B400 360deg)',
      filter: 'blur(7.5px)'
    }
  },
  bgColorStyle: {
    light: {
      background:
        'linear-gradient(0deg, rgba(255, 255, 255, 0.68) 0%, rgba(255, 255, 255, 0.68) 100%), radial-gradient(50% 50% at 50% 50%, #FFF 0%, rgba(255, 255, 255, 0.00) 100%), conic-gradient(from 135deg at 50% 50%, #FFEFB5 0deg, #EAB0FA 102.59999871253967deg, #90ECFF 232.19999313354492deg, #FFEFB5 360deg)'
    },
    dark: {
      background:
        'linear-gradient(0deg, rgba(0, 0, 0, 0.60) 0%, rgba(0, 0, 0, 0.60) 100%), radial-gradient(40% 40% at 50% 50%, rgba(255, 255, 255, 0.90) 0%, rgba(255, 255, 255, 0.00) 100%), conic-gradient(from 135deg at 50% 50%, #E5B400 0deg, #CB3AF3 102.6deg, #03D3FF 232.2deg, #E5B400 360deg)'
    }
  },
  textColorStyle: {
    light: {
      background:
        'radial-gradient(50% 50% at 50% 50%, rgba(255, 255, 255, 0.88) 0%, rgba(255, 255, 255, 0.00) 100%), conic-gradient(from 135deg at 50% 50%, #E5B400 0deg, #CB3AF3 102.59999871253967deg, #03D3FF 232.19999313354492deg, #E5B400 360deg)',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent'
    },
    dark: {
      background:
        'linear-gradient(#ffffff40, #ffffff40), conic-gradient(from 135deg at 50% 50%, #E5B400 0deg, #CB3AF3 75deg, #CB3AF3 100deg, #03D3FF 220deg, #03D3FF 240deg, #E5B400 360deg)',
      backgroundClip: 'text, text',
      WebkitBackgroundClip: 'text, text',
      WebkitTextFillColor: 'transparent',
      backgroundSize: '100% 130%, 100% 130%'
    }
  }
};